import { z } from 'zod'
import { DateTimeStringSchema } from './common'
import { GiftCodeDataSchema, PrizeHistoryDataSchema } from './reward'

/** PrizeSummaryData: 一覧API で返される賞品サマリー */
export const PrizeSummaryDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.enum(['ITEM', 'DLC', 'AR_PHOTO', 'COUPON', 'GIFT_CODE', 'GIFTEE']),
  fulfillmentType: z.string(),
  contentFile: z.string().nullable(),
  /**
   * ギフトコード情報（type=GIFT_CODE かつ status=EXCHANGED の場合のみ返される）。
   * ACHIEVED 状態では返されない。
   */
  code: GiftCodeDataSchema.nullable().optional(),
  /** クーポン画像URL（詳細API が PrizeData を返す場合のみ。COUPON タイプのみ） */
  image: z.string().nullable().optional(),
  /**
   * クーポン使用開始日。"YYYY/MM/DD" 形式（詳細APIが PrizeData を返す場合のみ）。
   * 一覧APIでは返されないためoptional。
   */
  startAt: z
    .string()
    .regex(/^\d{4}\/\d{2}\/\d{2}$/, '"YYYY/MM/DD" 形式で入力してください')
    .nullable()
    .optional(),
  /**
   * クーポン使用終了日。"YYYY/MM/DD" 形式（詳細APIが PrizeData を返す場合のみ）。
   * 一覧APIでは返されないためoptional。
   */
  endAt: z
    .string()
    .regex(/^\d{4}\/\d{2}\/\d{2}$/, '"YYYY/MM/DD" 形式で入力してください')
    .nullable()
    .optional(),
})
export type PrizeSummaryData = z.infer<typeof PrizeSummaryDataSchema>

export const UserProfileDataSchema = z.object({
  id: z.number(),
  username: z.string().nullable(),
  email: z.string().nullable(),
  nickname: z.string().nullable(),
  gender: z.string().nullable(),
  birthday: z.string().nullable(),
  zipCode: z.string().nullable(),
  area: z.string().nullable(),
  age: z.string().nullable(),
  round: z.number().nullable(),
  status: z.number().nullable(),
  recoveryCode: z.string().nullable(),
  createdAt: DateTimeStringSchema.nullable(),
  updatedAt: DateTimeStringSchema.nullable(),
})

export const UserRewardDataSchema = z.object({
  id: z.number(),
  presentId: z.number().nullable(),
  status: z.number().nullable(),
  createdAt: DateTimeStringSchema.nullable(),
  updatedAt: DateTimeStringSchema.nullable(),
})

const SpotInStampDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  image: z.string().nullable(),
})

/**
 * スタンプ獲得不可の理由コード。
 * 獲得API `POST /api/v2/stamps/{id}` が返す `error.code` と同じ語彙・同じ判定順序。
 */
export const StampUnavailableReasonSchema = z.enum([
  'CAMPAIGN_PERIOD_OUT',
  'STAMP_PERIOD_OUT',
  'STAMP_DAY_OUT',
  'STAMP_HOURS_OUT',
  'STAMP_NOT_AVAILABLE',
  'SHEET_LOCKED',
  'STAMP_ALREADY_ACQUIRED',
  'STAMP_ALREADY_ACQUIRED_TODAY',
])
export type StampUnavailableReason = z.infer<typeof StampUnavailableReasonSchema>

export const UserStampDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  image: z.string().nullable(),
  spots: z.array(SpotInStampDataSchema),
  /**
   * このスタンプ自体を取得済みか。
   * ボタンの活性/非活性には使わず isAvailable / unavailableReason を参照すること
   * （1日1回制限などで「未取得だが今は押せない」状態があるため）。
   */
  isAcquired: z.boolean(),
  acquiredCount: z.number(),
  /** 今この瞬間に獲得可能か。フィールド未対応のバックエンドに対しては従来どおり true 扱いにフォールバックする */
  isAvailable: z.boolean().optional().default(true),
  /** 獲得不可の理由。バックエンドが新コードを追加しても parse を壊さないよう未知値は null に縮退させる */
  unavailableReason: StampUnavailableReasonSchema.nullable().optional().default(null).catch(null),
})

/** GET /api/v2/user レスポンスの user オブジェクト（API が返す4フィールドのみ） */
export const UserGetProfileSchema = z.object({
  id: z.number(),
  nickname: z.string().nullable(),
  recoveryCode: z.string().nullable(),
  createdAt: DateTimeStringSchema.nullable(),
})
export type UserGetProfile = z.infer<typeof UserGetProfileSchema>

export const UserGetResponseSchema = z.object({
  user: UserGetProfileSchema,
})
export type UserGetResponse = z.infer<typeof UserGetResponseSchema>

export const UserRewardGetResponseSchema = z.object({
  reward: UserRewardDataSchema.nullable(),
})

export const UserRewardListResponseSchema = z.object({
  rewards: z.array(UserRewardDataSchema),
})

export const UserStampGetResponseSchema = z.object({
  stamp: UserStampDataSchema.nullable(),
})

export const UserStampListResponseSchema = z.object({
  stamps: z.array(UserStampDataSchema),
})

/** GET /api/v2/user/rewards レスポンスの条件進捗データ */
export const RewardConditionProgressDataSchema = z.object({
  displayName: z.string().nullable(),
  type: z.enum(['STAMP_COUNT', 'POINT_SUM', 'LOGIN_DAYS', 'USER_STATUS', 'QUEST_COMPLETE', 'CONDITION_GROUP']),
  current: z.number().int(),
  threshold: z.number().int(),
})

/** GET /api/v2/user/rewards レスポンスの認証済みユーザー特典獲得状況データ */
export const UserRewardStatusDataSchema = z.object({
  id: z.number(),
  /** 獲得トランザクションID（user_reward_transactions.id）。ACHIEVED/EXCHANGED/USED の場合のみ値が入る */
  transactionId: z.number().nullable(),
  name: z.string().nullable(),
  image: z.string().nullable(),
  /** UNOBTAINED / ACHIEVABLE / ACHIEVED / EXCHANGED / USED / UNAPPLIED / APPLIED / LOST / ENDED / SOLD_OUT */
  status: z.enum([
    'UNOBTAINED',
    'ACHIEVABLE',
    'ACHIEVED',
    'EXCHANGED',
    'USED',
    'UNAPPLIED',
    'APPLIED',
    'LOST',
    'ENDED',
    'SOLD_OUT',
  ]),
  /**
   * 賞品選択方式（SINGLE / GIVE_ALL / USER_SELECT / LOTTERY）。
   * SINGLE: 賞品が1つに確定しているため、ACHIEVED 時に再 acquire せず直接 exchange できる。
   */
  prizeSelectionType: z.enum(['SINGLE', 'GIVE_ALL', 'USER_SELECT', 'LOTTERY']).nullable().optional(),
  conditions: z.array(RewardConditionProgressDataSchema),
  createdAt: DateTimeStringSchema.nullable(),
  /**
   * 賞品サマリー一覧（一覧API /api/v2/user/rewards のみ返す。詳細APIでは返さない）。
   * 一覧APIでは PrizeSummaryData（code・applicationFormなし）の配列が返る。
   * 詳細APIでの prizes（PrizeHistoryData[]）は UserRewardStatusDetailDataSchema を参照。
   */
  prizes: z.array(PrizeSummaryDataSchema).optional().default([]),
})

/** GET /api/v2/user/rewards — 一覧レスポンス */
export const UserRewardStatusListResponseSchema = z.object({
  rewards: z.array(UserRewardStatusDataSchema),
})

/**
 * ユーザー特典詳細レスポンス用データ（詳細API専用）。
 * UserRewardStatusData との違い:
 * - prizes が PrizeHistoryData[]（獲得試行履歴。当選・ハズレ両方を含む）
 * - isRepeatable フィールドを持つ（複数回獲得可能フラグ）
 */
export const UserRewardStatusDetailDataSchema = z.object({
  id: z.number(),
  transactionId: z.number().nullable(),
  name: z.string().nullable(),
  image: z.string().nullable(),
  status: z.enum([
    'UNOBTAINED',
    'ACHIEVABLE',
    'ACHIEVED',
    'EXCHANGED',
    'USED',
    'UNAPPLIED',
    'APPLIED',
    'LOST',
    'ENDED',
    'SOLD_OUT',
  ]),
  prizeSelectionType: z.enum(['SINGLE', 'GIVE_ALL', 'USER_SELECT', 'LOTTERY']).nullable().optional(),
  conditions: z.array(RewardConditionProgressDataSchema),
  createdAt: DateTimeStringSchema.nullable(),
  prizes: z.array(PrizeHistoryDataSchema).optional().default([]),
  isRepeatable: z.boolean().optional().default(false),
})

/** GET /api/v2/user/rewards/{id} — 単件レスポンス */
export const UserRewardStatusGetResponseSchema = z.object({
  reward: UserRewardStatusDetailDataSchema.nullable(),
  // PR #2609: ルートの isLotteryLost / userRewardTransactionId は除去済み。
  // ハズレ判定は reward.prizes[].isLotteryLost を参照すること。
})

export type UserProfileData = z.infer<typeof UserProfileDataSchema>
export type UserRewardData = z.infer<typeof UserRewardDataSchema>
export type UserStampData = z.infer<typeof UserStampDataSchema>
export type UserRewardGetResponse = z.infer<typeof UserRewardGetResponseSchema>
export type UserRewardListResponse = z.infer<typeof UserRewardListResponseSchema>
export type UserStampGetResponse = z.infer<typeof UserStampGetResponseSchema>
export type UserStampListResponse = z.infer<typeof UserStampListResponseSchema>
export type RewardConditionProgressData = z.infer<typeof RewardConditionProgressDataSchema>
export type UserRewardStatusData = z.infer<typeof UserRewardStatusDataSchema>
export type UserRewardStatusDetailData = z.infer<typeof UserRewardStatusDetailDataSchema>
export type UserRewardStatusListResponse = z.infer<typeof UserRewardStatusListResponseSchema>
export type UserRewardStatusGetResponse = z.infer<typeof UserRewardStatusGetResponseSchema>

/** GET /api/v2/user/activities — スタンプ履歴データ */
export const StampActivityDataSchema = z.object({
  id: z.number().nullable(),
  name: z.string().nullable(),
  image: z.string().nullable(),
  spotName: z.string().nullable(),
  /** このスタンプが属する有効シートの種別ユニーク一覧 */
  sheetTypes: z.array(z.enum(['stamp_rally', 'point_rally', 'point_card', 'overlay'])).default([]),
})

/** GET /api/v2/user/activities — 特典履歴データ */
export const RewardActivityDataSchema = z.object({
  transactionId: z.number(),
  name: z.string().nullable(),
  status: z.enum(['ACHIEVED', 'EXCHANGED', 'USED', 'UNAPPLIED', 'APPLIED', 'LOST']),
  prizeName: z.string().nullable(),
  prizeType: z.enum(['ITEM', 'DLC', 'AR_PHOTO', 'COUPON', 'GIFT_CODE', 'GIFTEE']).nullable(),
  image: z.string().nullable(),
})

/** GET /api/v2/user/activities — クーポン履歴データ */
export const CouponActivityDataSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  spotName: z.string().nullable(),
  image: z.string().nullable(),
})

/** GET /api/v2/user/activities — 行動履歴1件 */
export const ActivityItemSchema = z.object({
  type: z.enum(['STAMP', 'REWARD', 'COUPON']),
  activityAt: z.string(),
  stamp: StampActivityDataSchema.nullable(),
  reward: RewardActivityDataSchema.nullable(),
  coupon: CouponActivityDataSchema.nullable(),
})

/** GET /api/v2/user/activities — レスポンス */
export const UserActivitiesResponseSchema = z.object({
  activities: z.array(ActivityItemSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})

export type StampActivityData = z.infer<typeof StampActivityDataSchema>
export type RewardActivityData = z.infer<typeof RewardActivityDataSchema>
export type CouponActivityData = z.infer<typeof CouponActivityDataSchema>
export type ActivityItem = z.infer<typeof ActivityItemSchema>
export type UserActivitiesResponse = z.infer<typeof UserActivitiesResponseSchema>
